import { Button, cn } from '@exam-prep/ui';
import type { ReactNode } from 'react';
import { ApiError } from '../lib/api';
import { friendlyMessage } from '../lib/api-errors';

export function Loading({ what = 'this page' }: { what?: string }) {
  return (
    <div role="status" className="grid gap-2">
      <span className="sr-only">Loading {what}…</span>
      <span aria-hidden="true" className="brand-skeleton block h-4 w-2/3" />
      <span aria-hidden="true" className="brand-skeleton block h-4 w-1/2" />
      <span aria-hidden="true" className="brand-skeleton block h-24 w-full" />
    </div>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-2 rounded-lg border border-dashed p-5 text-sm">
      <p className="font-medium">{title}</p>
      {children ? <p className="text-muted-foreground">{children}</p> : null}
      {action}
    </div>
  );
}

/** Error with a "Try again" button (queries). */
export function QueryError({ error, retry }: { error: unknown; retry?: () => void }) {
  if (!error) return null;
  return (
    <div className="flex flex-col items-start gap-2">
      <ErrorBox error={error} />
      {retry ? (
        <Button variant="outline" size="sm" onClick={retry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = friendlyMessage(error);
  const issues = error instanceof ApiError ? error.issues : [];
  return (
    <div
      role="alert"
      className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
    >
      <p className="font-medium">{msg}</p>
      {issues.length ? (
        <ul className="mt-2 list-disc space-y-0.5 pl-5 font-mono text-xs">
          {issues.map((i, n) => (
            <li key={n}>
              <span className="font-semibold">{i.path}</span>: {i.message}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function Notice({
  children,
  tone = 'info',
}: {
  children: ReactNode;
  tone?: 'info' | 'warn' | 'ok';
}) {
  const cls = {
    info: 'border-blue-200 bg-blue-50 text-blue-900',
    warn: 'border-amber-200 bg-amber-50 text-amber-900',
    ok: 'border-green-200 bg-green-50 text-green-900',
  }[tone];
  return <div className={cn('rounded-md border p-3 text-sm', cls)}>{children}</div>;
}
